'use client'

import { Suspense, startTransition, useMemo } from 'react'

import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'

import type { BlogPost, PaginatedResponse } from '@types'

import { useUpdateEffect } from 'react-use'
import { SWRConfig } from 'swr'
import { unstable_serialize } from 'swr/infinite'

import useSWRInfinite from '@hooks/client/useSWRInfinite'
import { queryString } from '@lib/helpers/client'

import PostsGrid from './posts-grid'
import Title from './title'

import styles from './post-list.module.scss'
import PostCard from '@components/post-card'
import useLocale from '@hooks/client/useLocale'

// const PostCard = dynamic(() => import('@components/post-card'), { ssr: true })
const LoadMore = dynamic(() => import('./load-more'), { ssr: false })

type PostListProps = {
  posts: PaginatedResponse<BlogPost[]>
}

function PostList(): JSX.Element {
  const locale = useLocale()
  const query = useSearchParams()
  const getApiKey = (i: number): string => {
    return `/blogs/posts?${queryString({
      page: i + 1 ?? query?.get('page'),
      query: query?.get('query'),
      category: query?.get('category'),
    })}`
  }

  const { data, size, setSize, mutate } = useSWRInfinite<PaginatedResponse<BlogPost[]>>(
    (i: number) => [getApiKey(i)],
    undefined,
    {
      revalidateFirstPage: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: false,
      revalidateIfStale: false,
      suspense: true,
    },
  )

  // Refetch on category or search update
  useUpdateEffect(() => {
    startTransition(() => {
      if (size > 1) {
        setSize(1)
      }
      mutate()
    })
  }, [query])

  const showLoadMore = useMemo((): boolean => {
    const lastElem = data?.[data.length - 1]

    if (size === 1) {
      return true
    }

    return (lastElem?.totalElements || 1 / 9) / (lastElem?.totalPages || 1) >= 1
  }, [data, size])

  return (
    <>
      <Suspense>
        <Title />
      </Suspense>
      <div className={styles.container}>
        {Object.values(data ?? {})[0]?.content.length !== 0 ? (
          Object.values(data ?? {}).map((pts, i) => (
            <PostsGrid key={i + 1} page={i + 1}>
              {pts?.content.map((post) => <PostCard key={post.id} post={post} country={locale} />)}
            </PostsGrid>
          ))
        ) : (
          <h3>No blog post found matching your search.</h3>
        )}
      </div>
      {showLoadMore && (
        <Suspense>
          <LoadMore setSize={setSize} size={size} />
        </Suspense>
      )}
    </>
  )
}

export default function InfiniteScroll({ posts }: PostListProps): JSX.Element {
  const query = useSearchParams()
  const getApiKey = (i: number): string => {
    return `/blogs/posts?${queryString({
      page: i + 1 ?? query?.get('page'),
      query: query?.get('query'),
      category: query?.get('category'),
    })}`
  }
  return (
    <SWRConfig value={{ fallbackData: { [unstable_serialize((i) => getApiKey(i))]: posts }, fallback: {} }}>
      <PostList />
    </SWRConfig>
  )
}
